// Sessions: start, status, timeline, comp and end (F-SES-01, -04, -10, -11, -14, -16, -21, -26).
//
// A **session** is one player using one station, from sign-in to sign-out (spec/05). This file is the
// session spine's HTTP surface — the foundation the M3–M6 billing stack charges against:
//
//   POST …/sessions                     staff assign a player to a station (F-SES-04)
//   GET  …/sessions/{sessionId}          status, time left and the current source (F-SES-11)
//   GET  …/sessions/{sessionId}/timeline the billed segments, with source and cost (F-SES-21)
//   POST …/sessions/{sessionId}/comp     grant free comp time, with a reason (F-SES-16, F-SES-26)
//   POST …/sessions/{sessionId}/end      end the session (F-SES-14)
//   POST /v1/station/sessions            the kiosk sign-in start, station-authenticated (F-SES-01)
//   POST /v1/station/sessions/current/end the player logs out at the PC (F-SES-14)
//
// Money is charged as consecutive **segments**, each paid by one source at one rate, posted to the
// ledger in integer minor units (spec/05 §4.3, spec/06 §2). Time left is computed from the funding
// plan, never stored (spec/05 §1). The spine funds a session two ways — **comp** (free seconds) and
// **postpaid** pay-as-you-go at the station class's rate (charged to the player's on-account
// receivable) — and leaves passes, the wallet, memberships and rewards as follow-on sources.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { GamePlayIdSchema, GuestIdSchema, JournalEntryIdSchema, LibraryEntryIdSchema, OrganizationIdSchema, PlayerIdSchema, PromotionIdSchema, ReservationIdSchema, SessionEventIdSchema, SessionIdSchema, SessionSegmentIdSchema, StationIdSchema, VenueIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { GuestTicketCodeSchema } from "../primitives/guest-ticket.js";
import { MoneySchema, NonNegativeMoneySchema } from "../primitives/money.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IdempotencyKeySchema, IsoDateTimeSchema } from "../primitives/scalars.js";
import { SESSION_PIN_MAX_LENGTH, SESSION_PIN_MIN_LENGTH, SESSION_PIN_PATTERN, SESSION_UNLOCK_METHODS } from "../primitives/session-pin.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION, UNLIMITED_BY_EXTENSION } from "./audit.js";
import { IdempotencyHeaderSchema } from "./id-checks.js";
import { GAME_PLAY_DETECTION_SOURCES } from "./game-plays.js";
import { StationSessionWalletSchema } from "./wallet.js";
import { ORDERS_CREATE, SESSIONS_COMP, SESSIONS_COMP_UNLIMITED, SESSIONS_END, SESSIONS_LOCK, SESSIONS_MOVE, SESSIONS_READ, SESSIONS_START, STATIONS_MESSAGE } from "../permissions/index.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station provides its ' +
    'own organisation, venue and station from the token; the body names only the player and the funding.';
// ---------------------------------------------------------------------------------------------
// Shared vocabulary (spec/05 §2, §4.1)
// ---------------------------------------------------------------------------------------------
/** The lifecycle states a session moves through (spec/05 §2). The spine drives a subset of them. */
export const SessionStatusSchema = z
    .enum(['starting', 'active', 'locked', 'paused', 'grace', 'ended'])
    .openapi('SessionStatus', { description: 'Where the session is in its lifecycle (spec/05 §2).' });
/** Why a session ended (spec/05 §2). */
export const SessionEndReasonSchema = z
    .enum([
    'logout',
    'time_up',
    'grace_expired',
    'staff_ended',
    'lock_limit',
    'pause_limit',
    'station_lost',
    'offline_policy',
    'start_failed',
    'replaced_by_new_login',
    'account_blocked',
    'venue_closed',
    // The station ended the session itself and reported it (spec/05 §8.5): the lease ran out while it
    // was offline, or on reconnect it found the cloud no longer running the session it held.
    'time_up_offline',
    'station_reconciled',
    // The player logged out while the station could not reach the cloud; the station ended it and reported it
    // on reconnect (spec/05 §8.5; offline play, 2026-10-01).
    'logout_offline',
    // The player ended it from the "Not you? End it" link they were sent after signing in at the PC from a browser or a
    // phone (the station sign-in handoff, ADR-0051, F-KIO-20).
    'ended_by_player',
])
    .openapi('SessionEndReason', { description: 'The recorded reason a session ended (spec/05 §2).' });
/** A source that can fund a stretch of the plan (spec/05 §4.1). */
export const PlanSourceSchema = z
    .enum(['comp', 'reward', 'allowance', 'pass', 'wallet', 'postpaid', 'grace'])
    .openapi('PlanSource', { description: 'A funding source in the plan (spec/05 §4.1).' });
/** A source a billed segment can draw on (spec/04 §4 `session_segment.source`). */
export const SegmentSourceSchema = z
    .enum(['pass', 'allowance', 'comp', 'reward', 'wallet', 'postpaid', 'offline'])
    .openapi('SegmentSource', { description: 'The source a billed segment drew on (spec/04 §4).' });
/**
 * One stretch of the plan the lease carries: this source pays for this many seconds (spec/05 §4.4,
 * §8.2). Named `SessionPlannedSegment` on the wire, not `PlannedSegment`, so its generated C# DTO
 * (`Contracts.SessionPlannedSegment`) does not collide with the station's own
 * `Domain.PlannedSegment` (the `@clavian/domain` mirror the station already references unqualified).
 */
export const SessionPlannedSegmentSchema = z
    .object({
    source: PlanSourceSchema,
    label: z.string().min(1).max(120).nullable(),
    seconds: z.number().int().min(0),
})
    .openapi('SessionPlannedSegment');
/** What a session has been charged so far, by unit (spec/05 §4.3): time in seconds, money in minor units. */
export const SessionChargeSchema = z
    .object({
    /** Play seconds consumed from minute sources (comp today; passes and allowances later). */
    chargedSeconds: z.number().int().min(0),
    /** Money charged to on-account / the wallet for money segments (spec/06 §2.3). */
    chargedAmount: MoneySchema,
})
    .openapi('SessionCharge');
// ---------------------------------------------------------------------------------------------
// "How much time do I have?" — the funding breakdown (spec/05 §4.4, spec/06 §4; 2026-10-01)
// ---------------------------------------------------------------------------------------------
/** The kinds of source a breakdown lists (spec/05 §4.1): the plan's words, less grace, which follows the items. */
export const FundingBreakdownKindSchema = z
    .enum(['comp', 'pass', 'wallet', 'postpaid'])
    .openapi('FundingBreakdownKind', { description: 'A source of the session’s time: comp, a pass, the wallet at the rate, or time added on account.' });
/**
 * One source's share of the time left, in the order the session uses it (`@clavian/domain` `sessionFundingOrder`):
 * comp, the passes in their captured order, the wallet (before the passes when the player saved their pass), then
 * time added on account. A money source says what its seconds cost at the session's (undiscounted) rate.
 */
export const FundingBreakdownItemSchema = z
    .object({
    kind: FundingBreakdownKindSchema,
    /** Seconds of play this source still funds. */
    seconds: z.number().int().min(0),
    /** A pass's name ("Night Owl"); null for the other kinds. */
    name: z.string().nullable(),
    /** The pass holding's TypeID (`pah_…`); null for the other kinds. A plain string: a nullable reference here would
     * make the shared `PassHoldingId` component nullable in the generated clients. */
    holdingId: z.string().nullable(),
    /** When a pass stops being valid, when it has an end. */
    expiresAt: IsoDateTimeSchema.nullable(),
    /** When a pass's open window closes — its seconds stop counting then today. */
    windowClosesAt: IsoDateTimeSchema.nullable(),
    /** The hourly rate a money source is charged at (wallet, postpaid); null for minute sources. */
    hourlyRate: NonNegativeMoneySchema.nullable(),
    /** The billing unit that rate is charged in (60, 300, 900 or 1800 seconds); null for minute sources. */
    billingUnitSeconds: z.number().int().min(60).max(1800).nullable(),
    /** What these seconds cost at `hourlyRate`, undiscounted; null for minute sources. */
    amount: NonNegativeMoneySchema.nullable(),
})
    .openapi('FundingBreakdownItem');
/** What follows the funded time (spec/05 §4.5): the grace minutes, once, or the end. */
export const FundingBreakdownThenSchema = z
    .object({
    kind: z.enum(['grace', 'end']),
    /** The grace, in seconds; 0 when the session ends. */
    seconds: z.number().int().min(0),
})
    .openapi('FundingBreakdownThen');
/**
 * The session's time, source by source: "2:15 left — 1:30 Night Owl pass (expires 2 am) · 0:45 from wallet (₹45
 * at ₹60/h) · then 5 min grace". The items sum to `totalSeconds`, the time left before grace.
 */
export const FundingBreakdownSchema = z
    .object({
    items: z.array(FundingBreakdownItemSchema),
    totalSeconds: z.number().int().min(0),
    then: FundingBreakdownThenSchema,
})
    .openapi('FundingBreakdown', { description: 'What a session’s time is made of, in the order it is used (spec/05 §4.4).' });
// ---------------------------------------------------------------------------------------------
// Console sessions: booked blocks, controllers, one order at the end (ADR-0034)
// ---------------------------------------------------------------------------------------------
/**
 * One priced line of a staff-run session (ADR-0034, `@clavian/domain` `quoteConsoleSession`): the first controller of
 * a block (its block price, or the first-controller rate for its length), or the extra controllers over one part of a
 * block (the extra-controller rate; a controller change splits a block at the moment it took effect).
 */
export const ConsoleQuoteLineSchema = z
    .object({
    kind: z.enum(['first_controller', 'extra_controllers']),
    /** The booked block it belongs to, 0-based. */
    blockIndex: z.int().min(0),
    /** The block's booked length in minutes; null for an Open block (charged for the time played). */
    blockMinutes: z.int().min(1).nullable(),
    /** Where on the session's clock the line's stretch starts, and how long it is, in seconds. */
    fromSecond: z.int().min(0),
    seconds: z.int().min(0),
    /** Controllers the line charges for: 1 for the first controller, else the extras on this stretch. */
    quantity: z.int().min(1),
    /** What one controller costs for the stretch. */
    unitAmount: NonNegativeMoneySchema,
    /** `unitAmount × quantity`, before offers. */
    amount: NonNegativeMoneySchema,
    /** `block` when a fixed block price set the first controller's amount, else `rate`. */
    basis: z.enum(['block', 'rate']),
    /** What console offers took off this line. */
    discount: NonNegativeMoneySchema,
})
    .openapi('ConsoleQuoteLine');
/** A console offer that applied, with what it took off (spec/06 §8.4 rule 8). */
export const ConsoleQuoteOfferSchema = z
    .object({ promotionId: PromotionIdSchema, name: z.string().min(1).max(120), amount: NonNegativeMoneySchema })
    .openapi('ConsoleQuoteOffer');
/**
 * What a staff-run session costs (ADR-0034): the lines, the console offers taken off, and the total "due at the end".
 * The same quote the start form previews, "Add time" previews, the running session shows and the order is placed at.
 */
export const ConsoleQuoteSchema = z
    .object({
    lines: z.array(ConsoleQuoteLineSchema),
    offers: z.array(ConsoleQuoteOfferSchema),
    subtotal: NonNegativeMoneySchema,
    discount: NonNegativeMoneySchema,
    total: NonNegativeMoneySchema,
    /** Seconds charged for: the booked blocks, and an Open block's time played rounded up to the billing unit. */
    chargedSeconds: z.int().min(0),
    /** Whether the session is Open — no end, charged for the time played. */
    open: z.boolean(),
    /** The most controllers the session has had. */
    peakControllers: z.int().min(1),
    /** What an hour costs at the current controllers, before offers: an Open session's running rate. */
    hourlyAmount: NonNegativeMoneySchema,
    /** The billing unit an Open session's time played is rounded up to, in seconds. */
    billingUnitSeconds: z.int().min(1),
})
    .openapi('ConsoleQuote', { description: 'A staff-run session’s price: lines per controller, console offers, due at the end (ADR-0034).' });
/** A booked block of a staff-run session. */
export const SessionBookedBlockSchema = z
    .object({
    /** The block's length in minutes; null for Open. */
    minutes: z.int().min(1).nullable(),
    addedAt: IsoDateTimeSchema,
})
    .openapi('SessionBookedBlock');
/** From `fromSecond` of the session's clock (at `at`) the station has `controllers` controllers. */
export const SessionControllerChangeSchema = z
    .object({
    at: IsoDateTimeSchema,
    fromSecond: z.int().min(0),
    controllers: z.int().min(1),
})
    .openapi('SessionControllerChange');
/**
 * A staff-run session's booking (ADR-0034): its blocks, its controllers, and what it comes to now. Paid once — the
 * session places one order when it ends (`saleId`), taken at the counter.
 */
export const SessionBookingSchema = z
    .object({
    /** The controllers now. */
    controllers: z.int().min(1),
    /** Open: no end, charged for the time played. */
    open: z.boolean(),
    /** The booked blocks' seconds added up; 0 for an Open session. */
    bookedSeconds: z.int().min(0),
    blocks: z.array(SessionBookedBlockSchema),
    controllerChanges: z.array(SessionControllerChangeSchema),
    /** What is due at the end, priced now (an Open session: the time played so far). */
    quote: ConsoleQuoteSchema,
    /** The order the session placed when it ended (`sal_…`); null while it runs. A plain string, like `holdingId`. */
    saleId: z.string().nullable(),
})
    .openapi('SessionBooking');
// ---------------------------------------------------------------------------------------------
// The session, as a client sees it
// ---------------------------------------------------------------------------------------------
export const SessionSchema = z
    .object({
    id: SessionIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    stationId: StationIdSchema,
    /** The player. A walk-in on a staff-run station (ADR-0034) carries the nil id `WALK_IN_PLAYER_ID` and `walkIn: true`. */
    playerId: PlayerIdSchema,
    status: SessionStatusSchema,
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema.nullable(),
    endReason: SessionEndReasonSchema.nullable(),
    /** The source paying now, or `null` once the plan has run out (spec/05 §4.4). */
    currentSource: PlanSourceSchema.nullable(),
    /** Seconds until the funded end, computed from the plan, never stored (spec/05 §1, §4.4). */
    timeLeftSeconds: z.number().int().min(0),
    /** The clock time the funded time runs out, or `null` when nothing funds the session. */
    fundedEndAt: IsoDateTimeSchema.nullable(),
    /** The plan from now forward — what pays, and for how long (spec/05 §4.4). */
    plan: z.array(SessionPlannedSegmentSchema),
    charged: SessionChargeSchema,
    /** The api's clock, RFC 3339 UTC: the client counts down `timeLeftSeconds` against it. */
    serverTime: IsoDateTimeSchema,
    /**
     * Whether the player has set a lock PIN for this session (spec/05 §6 rule 1). The PIN itself is never
     * returned. A station that restarted mid-session reads it to offer PIN unlock rather than PIN setup.
     */
    hasLockPin: z.boolean().optional(),
    /**
     * Seconds of this session the station ran while it could not reach the cloud and reported on reconnect
     * (spec/05 §8.5; the owner's offline-play decision, 2026-10-01): past the funded end under "keep running
     * sessions going", or a whole session a known player started offline. The console marks such a session
     * "Played offline". Absent or 0 for a session that never ran offline.
     */
    offlineSeconds: z.number().int().min(0).optional(),
    /**
     * What the time left is made of, source by source, in the order it is used, and what follows (spec/05 §4.4).
     * Absent on an ended session.
     */
    funding: FundingBreakdownSchema.optional(),
    /**
     * `true` on a session on a staff-run station (ADR-0033): no lease, no station commands; the server owns the timer
     * and alerts staff as time runs out. Absent on a session on a managed station (a PC with the station app).
     */
    staffRun: z.boolean().optional(),
    /** The grace window this session gets once its funded time runs out, in seconds (spec/05 §4.5). Absent when none. */
    graceSeconds: z.number().int().min(0).optional(),
    /** When grace ends and the session with it, RFC 3339 UTC — present only while the session is in grace. */
    graceEndsAt: IsoDateTimeSchema.optional(),
    /** The title staff picked when they started a staff-run session (ADR-0033). Absent: "console play". */
    staffTitle: z
        .object({ libraryEntryId: LibraryEntryIdSchema, name: z.string().min(1).max(200) })
        .optional()
        .openapi('SessionStaffTitle', { description: 'The library title staff picked at the start of a staff-run session.' }),
    /** `true` on a staff-run session started for a walk-in, with no player (ADR-0034): `playerId` is the nil id. */
    walkIn: z.boolean().optional(),
    /**
     * A guest's session (F-SES-06, F-SES-07, ADR-0059): played on a guest pass, started by its ticket code at the PC.
     * The guest is not a player, so `playerId` is the nil id until the ticket is claimed into an account; after that it
     * is the claiming player's, and `guest` still says the session was played as a guest. Shown as "Guest · ABC123".
     * Absent on every other session.
     */
    guest: z
        .object({ id: GuestIdSchema, code: GuestTicketCodeSchema })
        .optional()
        .openapi('SessionGuest', { description: 'The guest a session was played by, and their ticket code.' }),
    /**
     * A staff-run session's booked blocks and controllers, and what is due at the end (ADR-0034). Absent on a session on a
     * managed station, and on a staff-run session started before console sessions were booked.
     */
    booking: SessionBookingSchema.optional(),
})
    .openapi('Session', { description: 'A player using a station, with its funding plan and time left (spec/05).' });
/** One billed stretch of the session (spec/04 §4 `session_segment`, F-SES-10, F-SES-21). */
/**
 * A `play` promotion (a happy hour on pay-as-you-go & wallet play, F-POS-03, spec/06 §8.4) that discounted some of a
 * segment's billing units: its name, its percentage and what it has taken off so far — "Happy hour −20%".
 */
export const SessionSegmentPromotionSchema = z
    .object({
    promotionId: PromotionIdSchema,
    name: z.string().min(1).max(120),
    percentBps: z.int().min(1).max(10_000),
    amount: MoneySchema,
})
    .openapi('SessionSegmentPromotion');
/** A segment's rate snapshot (F-SES-21): the hourly amount and the billing unit a started unit is charged in full. */
export const SessionSegmentRateSchema = z
    .object({
    hourlyAmount: MoneySchema,
    billingUnitSeconds: z.int().min(1),
})
    .openapi('SessionSegmentRate');
export const SessionSegmentSchema = z
    .object({
    id: SessionSegmentIdSchema,
    stationId: StationIdSchema,
    source: SegmentSourceSchema,
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema.nullable(),
    chargedSeconds: z.number().int().min(0),
    /** What the segment has cost the player so far: the billing units at the rate, less `discount`. */
    chargeAmount: MoneySchema,
    /** What `play` promotions took off the segment's billing units so far (spec/06 §8.4); zero for most segments. */
    discount: MoneySchema,
    /** The `play` promotions that discounted it, each with what it took off. Empty for most segments. */
    promotions: z.array(SessionSegmentPromotionSchema),
    /** The journal entry this segment's last charge posted to, for tracing money to the ledger. */
    ledgerEntryId: JournalEntryIdSchema.nullable(),
    /**
     * The rate the segment is charged at, snapshot when it opened (F-SES-21 rule 2): the hourly amount and the billing
     * unit. `null` for a minute source — comp and pass time cost no money per second.
     */
    rate: SessionSegmentRateSchema.nullable().optional(),
})
    .openapi('SessionSegment');
/**
 * A kind of timeline entry (spec/05 §12, F-SES-21). The spine records the milestones and the staff
 * actions it drives, the low-time `warned` entries it pushes to the station (F-SES-11), and the
 * `locked`/`unlocked` entries the station reports when the player steps away or staff lock remotely
 * (F-SES-12, F-SES-15), and `pass_added` when a pass bought from the kiosk is queued onto the running
 * session (F-KIO-14); the remaining station-sourced entries — titles played, connectivity gaps —
 * join the enum as the events that carry them land (spec/05 §7, §12). `wallet_enabled` is the player's
 * "Keep playing with wallet" from the kiosk's quick purchase (F-KIO-13), or their "Use my wallet for play time" turned
 * on; `wallet_disabled` is that switch turned off mid-session (F-WAL-04) — the wallet stops paying for play.
 */
export const SessionEventKindSchema = z
    .enum([
    'started',
    'comped',
    'sold_time',
    'pass_added',
    'message_sent',
    'warned',
    'locked',
    'unlocked',
    'moved',
    'grace_started',
    'ended',
    'offline_settled',
    'wallet_enabled',
    // A staff-run session's booking (ADR-0034): a block added, the controllers changed, the order placed at the end.
    'time_added',
    'controllers_changed',
    'order_placed',
    // The player turned "Use my wallet for play time" off mid-session (F-WAL-04, spec/05 §4.1).
    'wallet_disabled',
])
    .openapi('SessionEventKind', { description: 'What happened at one point in a session (spec/05 §12).' });
/** Who caused a timeline entry: a staff member, the station/device, the player, or the system clock. */
export const SessionEventActorSchema = z
    .enum(['member', 'user', 'device', 'player', 'system'])
    .openapi('SessionEventActor', { description: 'The kind of actor behind a timeline entry (spec/07 §11).' });
/**
 * One entry in a session's ordered history (spec/05 §12, F-SES-21): when it happened, who did it, and
 * a small detail bag the client renders per `kind` (the comp minutes and reason, the message text, the
 * end reason). `detail` is an open object so a new kind adds a field without a contract change.
 */
export const SessionEventSchema = z
    .object({
    id: SessionEventIdSchema,
    kind: SessionEventKindSchema,
    at: IsoDateTimeSchema,
    actorType: SessionEventActorSchema,
    /** The member, user, device or player behind it; `null` for the system clock. */
    actorId: z.string().min(1).max(64).nullable(),
    detail: z.record(z.string(), z.unknown()),
})
    .openapi('SessionEvent');
/**
 * One title played in the session (F-SES-21 rule 4, spec/04 §4 `game_play`): the name it had, how the station knew
 * it (`detectedBy`; `process` for a station older than 2026-10-01; `staff` for the title staff picked on a staff-run
 * station, ADR-0033), and the seconds its process ran (≤ 24 h, M-11).
 */
export const SessionTimelinePlaySchema = z
    .object({
    id: GamePlayIdSchema,
    name: z.string(),
    detectedBy: z.enum([...GAME_PLAY_DETECTION_SOURCES, 'process', 'staff']),
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema,
    seconds: z.int().min(0),
})
    .openapi('SessionTimelinePlay');
/**
 * A stretch the station ran without the cloud (F-SES-21 rule 5), from what it reported on reconnect:
 *
 *   end_report    the station ended the session while offline (`station.session.ended` with its offline and overrun
 *                 seconds) — the overrun past the funded end, less the grace credit, is what was charged;
 *   offline_code  a staff offline code it accepted (spec/05 §8.4) — the action and the seconds played under it;
 *   offline_session a whole session a known player started offline (spec/05 §8.4b).
 *
 * `measuredSeconds` is the station's own monotonic measurement; `policy` is the venue's offline play as the lease
 * carried it when the gap was settled (`continue_and_settle`, `continue_to_lease_end`, `lock_immediately`), or
 * `null` when it was not recorded.
 */
export const SessionTimelineOfflineSchema = z
    .object({
    kind: z.enum(['end_report', 'offline_code', 'offline_session']),
    /** When the offline stretch began, when it can be known (`endedAt − measuredSeconds`); `null` otherwise. */
    from: IsoDateTimeSchema.nullable(),
    /** When it was settled (the report, or the code's use). */
    at: IsoDateTimeSchema,
    measuredSeconds: z.int().min(0),
    overrunSeconds: z.int().min(0).nullable(),
    graceCreditSeconds: z.int().min(0).nullable(),
    chargedSeconds: z.int().min(0).nullable(),
    chargedAmount: MoneySchema.nullable(),
    /** The offline code's action (`start`, `extend`, `unlock`), for an `offline_code`; `null` otherwise. */
    codeAction: z.string().nullable(),
    policy: z.string().nullable(),
})
    .openapi('SessionTimelineOffline');
/**
 * One journal entry the session posted (F-SES-21 rule 6): its costs come from the ledger, never a mutable flag, so a
 * corrected charge appears as its reversing entry **beside** the original, never as an edited line. `amount` is the
 * money it moved to play revenue (negative for a reversal), `seconds` the play time it consumed (negative for a
 * reversal); either is zero when the entry moved none.
 */
export const SessionTimelineLedgerEntrySchema = z
    .object({
    id: JournalEntryIdSchema,
    kind: z.string(),
    at: IsoDateTimeSchema,
    memo: z.string().nullable(),
    reversal: z.boolean(),
    amount: MoneySchema.nullable(),
    seconds: z.int(),
})
    .openapi('SessionTimelineLedgerEntry');
export const SessionTimelineSchema = z
    .object({
    sessionId: SessionIdSchema,
    segments: z.array(SessionSegmentSchema),
    /** The ordered event history — staff actions, state changes and milestones, oldest first (F-SES-21). */
    events: z.array(SessionEventSchema),
    /** The titles played in the session, oldest first (F-SES-21 rule 4). */
    plays: z.array(SessionTimelinePlaySchema).optional(),
    /** The connectivity gaps the station reported, oldest first (F-SES-21 rule 5). */
    offline: z.array(SessionTimelineOfflineSchema).optional(),
    /** The session's journal entries — charges, grants and their reversals — oldest first (F-SES-21 rule 6). */
    ledger: z.array(SessionTimelineLedgerEntrySchema).optional(),
})
    .openapi('SessionTimeline', {
    description: 'A session’s billed segments and its ordered event history, oldest first (F-SES-21).',
});
/**
 * Which of the session's funding sources an entry drew on (F-SES-21 rule 6): `pass` (a pass's time), `comp` (free
 * time), `wallet` (the player's wallet) or `postpaid` (pay-as-you-go, charged on account). Read from the accounts
 * the entry's lines moved; absent for an entry that drew on none of them (a play-time promotion's contra entry).
 */
export const SESSION_LEDGER_FUNDING = ['pass', 'comp', 'wallet', 'postpaid'];
/**
 * One journal entry the session posted, as its paginated ledger lists it (F-SES-21 rule 6): the timeline's ledger
 * entry with the funding source that paid it, and `amount` always present, zero when the entry moved no play
 * revenue (a minute covered by a pass is a charge of zero money, not a missing one).
 */
export const SessionLedgerEntrySchema = z
    .object({
    id: JournalEntryIdSchema,
    kind: z.string(),
    at: IsoDateTimeSchema,
    memo: z.string().nullable(),
    reversal: z.boolean(),
    /** The money it moved to play revenue, in the session's currency: negative for a reversal, zero for none. */
    amount: MoneySchema,
    /** The play seconds it consumed: negative for a reversal, zero for none. */
    seconds: z.int(),
    /** The source that paid it; absent for an entry that drew on none (a play-time promotion's contra entry). */
    funding: z.enum(SESSION_LEDGER_FUNDING).optional(),
})
    .openapi('SessionLedgerEntry');
export const SessionLedgerPageSchema = paginated(SessionLedgerEntrySchema, 'SessionLedgerPage');
// ---------------------------------------------------------------------------------------------
// Funding a session at start (spec/05 §4.1)
// ---------------------------------------------------------------------------------------------
/**
 * Why comp time was granted (spec/05 §7, F-SES-16): compensation (a crashed PC, a fault), a promotion, staff
 * play, or `other` — which needs a `note` saying why. The console's list and the server's are this one enum.
 */
export const COMP_REASONS = ['compensation', 'promo', 'staff_play', 'other'];
export const CompReasonSchema = z.enum(COMP_REASONS).openapi('CompReason', { description: 'Why comp time was granted (spec/05 §7, F-SES-16).' });
/** The free-text note a comp carries; required with reason `other` (F-SES-16). */
export const CompNoteSchema = z.string().trim().min(1).max(200);
/**
 * How a session is funded at start (spec/05 §4.1). `kind` selects the source, and the fields it
 * needs come with it:
 *
 *   comp      free play: `minutes` of it, with a `reason` (F-SES-16). Bounded server-side by
 *             `sessions.compMaxMinutesPerAction` and the shift limit (spec/07 §6.4).
 *   postpaid  pay-as-you-go at the station class's rate, charged to the player's on-account
 *             receivable (spec/06 §4, §8.6). `budgetAmount` is what the player is authorised to
 *             spend this session; the plan funds as many whole billing units as it buys, and grace
 *             or the end follows when it is spent.
 *
 * A flat object rather than a discriminated union, so the generated TypeScript client and the C#
 * station DTOs stay in the subset the codegen supports; the server checks that the fields the `kind`
 * needs are present and refuses the rest with `422`.
 */
export const FundingChoiceSchema = z
    .object({
    kind: z.enum(['comp', 'postpaid']),
    /** Comp only: minutes of free play. */
    minutes: z.number().int().min(1).max(1440).optional(),
    /** Comp only: why the comp was granted, from the list (F-SES-16). */
    reason: CompReasonSchema.optional(),
    /** Comp only: a note — required with reason `other`. */
    note: CompNoteSchema.optional(),
    /** Postpaid only: what the player is authorised to spend on account this session. */
    budgetAmount: NonNegativeMoneySchema.optional(),
})
    .openapi('FundingChoice', { description: 'How a session is funded at start: comp, or postpaid pay-as-you-go.' });
/**
 * Which source the player wants used first when they hold both a pass that covers this station and a
 * wallet that funds play (F-SES-09, spec/05 §4.2): `pass` (the default — the plan's own order) or
 * `wallet` ("use my wallet, save my pass": the wallet is drawn before the passes).
 */
export const SessionFundingPreferenceSchema = z
    .enum(['pass', 'wallet'])
    .openapi('SessionFundingPreference', { description: 'Pass first (the default) or wallet first, for a player who holds both (spec/05 §4.2).' });
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{organizationId}/sessions  — staff assign (F-SES-04)
// ---------------------------------------------------------------------------------------------
/**
 * How a staff-run session is booked (ADR-0034): the controllers (1 up to the station's capacity) and the block —
 * `minutes` (30, 60, 120, 300 or a custom length), or `null` for **Open** (no end, charged for the time played).
 */
export const SessionBookingRequestSchema = z
    .object({
    controllers: z.int().min(1).max(16),
    minutes: z.int().min(1).max(1440).nullable(),
})
    .openapi('SessionBookingRequest');
export const StartSessionSchema = z
    .object({
    stationId: StationIdSchema,
    /** The player. Required on a managed station; optional on a staff-run one — absent is a walk-in (ADR-0034). */
    playerId: PlayerIdSchema.optional(),
    /**
     * How a session on a **managed** station is funded — required there. Refused on a staff-run station: it is booked
     * (`booking`) and paid once, at the end (ADR-0034).
     */
    funding: FundingChoiceSchema.optional(),
    /** A staff-run station's booking — required there, refused on a managed station (ADR-0034). */
    booking: SessionBookingRequestSchema.optional(),
    /**
     * The reservation this sign-in checks in against (F-SES-05, spec/05 §10). Optional: a walk-in
     * names none. Recorded on the session's `started` timeline entry so a reservation and its session
     * are linked. The `reservations` module that validates the id and closes the hold is a follow-on
     * (F-RES); until it lands the spine records the reference without enforcing it.
     */
    reservationId: ReservationIdSchema.optional(),
    /** Pass first (the default) or wallet first, when the player holds both (spec/05 §4.2). */
    prefer: SessionFundingPreferenceSchema.optional(),
    /**
     * On a staff-run station (ADR-0033): the venue-library title the player is playing, so Games played and the
     * player's history count it — one play for the time played, recorded when the session ends. Omit for "console
     * play" (nothing recorded). Ignored on a managed station, whose app detects what is played (spec/10 §7.3).
     */
    libraryEntryId: LibraryEntryIdSchema.optional(),
})
    .openapi('StartSession');
const SessionParamsSchema = OrganizationScopeParamsSchema.extend({
    sessionId: SessionIdSchema,
}).openapi('SessionParams');
export const startSessionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sessions',
    operationId: 'startSession',
    tags: ['sessions'],
    summary: 'Assign a player to a station (F-SES-04)',
    description: 'Staff start a per-minute session on a station — the only way to start a manual station, and the ' +
        'path for a player without a phone (spec/05 §3.1). The venue is named in the path (like the station ' +
        'session reads), so the station the body names is one of that venue’s. The station must be free ' +
        'and enabled; the player must have no other active session. On a managed station it is funded as comp or as ' +
        'postpaid pay-as-you-go at the class rate. On a **staff-run** station (ADR-0034) it is booked instead — controllers ' +
        'and a block, or Open — for a player or a walk-in, and paid once when it ends: one order at the counter.',
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: StartSessionSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: SESSIONS_START,
    responses: {
        201: jsonResponse(SessionSchema, 'The session, with its plan and time left.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Not allowed, or the player does not meet the station class’s age limit (spec/05 §3.2 check 5).', 'forbidden', 'second-factor-required', 'age-restricted'),
        402: problemResponse('Nothing funds the session (spec/05 §3.2 check 8).', 'no-funding'),
        404: problemResponse('No such station or player in this organisation.', 'not-found'),
        409: problemResponse('The station is unavailable, or the player is already in a session.', 'station-unavailable', 'already-in-session'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Console sessions (ADR-0034): the start preview, the change preview, add time, change controllers
// ---------------------------------------------------------------------------------------------
/** What the start form previews: a booking on a staff-run station, for a player or a walk-in. */
export const QuoteConsoleSessionSchema = z
    .object({
    stationId: StationIdSchema,
    booking: SessionBookingRequestSchema,
    /** The player, when one is picked: a players-only offer applies only then. */
    playerId: PlayerIdSchema.optional(),
})
    .openapi('QuoteConsoleSession');
export const quoteConsoleSessionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sessions/quote',
    operationId: 'quoteConsoleSession',
    tags: ['sessions'],
    summary: 'Preview the price of a staff-run session (ADR-0034)',
    description: 'Prices a booking on a staff-run station — controllers and a block, or Open — from the rate card in force for its ' +
        'class, with the console offers that would apply now: the lines per controller, the offers and the total due at the ' +
        'end. Writes nothing. The same function prices the order placed when the session ends.',
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: QuoteConsoleSessionSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: SESSIONS_START,
    responses: {
        200: jsonResponse(ConsoleQuoteSchema, 'The price of the booking.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such station or player in this venue.', 'not-found'),
        409: problemResponse('The station is not staff-run, or the controllers are more than it takes.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** A change to a running staff-run session to preview: a block to add, and/or the controllers from now on. */
export const QuoteSessionChangeSchema = z
    .object({
    addMinutes: z.int().min(1).max(1440).optional(),
    controllers: z.int().min(1).max(16).optional(),
})
    .openapi('QuoteSessionChange');
/** What a change would do to what is due at the end: the quote now, the quote after, and the difference. */
export const SessionChangeQuoteSchema = z
    .object({
    current: ConsoleQuoteSchema,
    next: ConsoleQuoteSchema,
    /** `next.total − current.total`: what the change adds (or takes off). */
    difference: MoneySchema,
})
    .openapi('SessionChangeQuote');
export const quoteSessionChangeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/quote',
    operationId: 'quoteSessionChange',
    tags: ['sessions'],
    summary: 'Preview adding time or changing controllers on a staff-run session (ADR-0034)',
    description: 'Prices the running session as it is and as it would be after the change — a block added, the controllers from now on. Writes nothing.',
    request: {
        params: SessionParamsSchema,
        body: { content: { 'application/json': { schema: QuoteSessionChangeSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    responses: {
        200: jsonResponse(SessionChangeQuoteSchema, 'The session’s price now and after the change.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        409: problemResponse('The session has ended, is not a booked staff-run session, is Open (no time to add), or the controllers are more than the station takes.', 'session-already-ended', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** A block to add to a running staff-run session (ADR-0034). */
export const AddSessionTimeSchema = z
    .object({ minutes: z.int().min(1).max(1440) })
    .strict()
    .openapi('AddSessionTime');
export const addSessionTimeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/add-time',
    operationId: 'addSessionTime',
    tags: ['sessions'],
    summary: 'Add a block to a staff-run session (ADR-0034)',
    description: 'Books one more block on a running staff-run session: the countdown moves out by its length (a session in grace ' +
        'returns to active) and the block is priced into the order placed at the end. Nothing is charged now. Audited.',
    request: {
        params: SessionParamsSchema,
        body: { content: { 'application/json': { schema: AddSessionTimeSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    responses: {
        200: jsonResponse(SessionSchema, 'The session, with the block added.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        409: problemResponse('The session has ended, is not a booked staff-run session, or is Open.', 'session-already-ended', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** The controllers a running staff-run session has from now on (ADR-0034). */
export const SetSessionControllersSchema = z
    .object({ controllers: z.int().min(1).max(16) })
    .strict()
    .openapi('SetSessionControllers');
export const setSessionControllersRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/controllers',
    operationId: 'setSessionControllers',
    tags: ['sessions'],
    summary: 'Change the controllers on a staff-run session (ADR-0034)',
    description: 'From this moment the session has this many controllers (1 up to the station’s capacity). The change is recorded ' +
        'at the session’s clock, so the order placed at the end prices each part with its own controllers. Audited.',
    request: {
        params: SessionParamsSchema,
        body: { content: { 'application/json': { schema: SetSessionControllersSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    responses: {
        200: jsonResponse(SessionSchema, 'The session, with its controllers from now on.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        409: problemResponse('The session has ended, is not a booked staff-run session, or the station takes fewer controllers.', 'session-already-ended', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/organizations/{organizationId}/sessions/{sessionId}  — status (F-SES-11)
// ---------------------------------------------------------------------------------------------
export const getSessionStatusRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}',
    operationId: 'getSessionStatus',
    tags: ['sessions'],
    summary: 'Read a session its status and time left (F-SES-11)',
    description: 'The session with its current source, time left and the plan from now forward (spec/05 §4.4).',
    request: { params: SessionParamsSchema },
    [PERMISSION_EXTENSION]: SESSIONS_READ,
    responses: {
        200: jsonResponse(SessionSchema, 'The session.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/organizations/{organizationId}/sessions/{sessionId}/timeline  — timeline (F-SES-21)
// ---------------------------------------------------------------------------------------------
export const getSessionTimelineRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/timeline',
    operationId: 'getSessionTimeline',
    tags: ['sessions'],
    summary: 'Read a session its billed segments (F-SES-21)',
    description: 'Every segment with its source, seconds and cost, oldest first — how the session was billed.',
    request: { params: SessionParamsSchema },
    [PERMISSION_EXTENSION]: SESSIONS_READ,
    responses: {
        200: jsonResponse(SessionTimelineSchema, 'The session’s billed segments.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET …/sessions/{sessionId}/ledger: the session's journal entries, newest first, a page at a time (F-SES-21)
// ---------------------------------------------------------------------------------------------
export const listSessionLedgerRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/ledger',
    operationId: 'listSessionLedger',
    tags: ['sessions'],
    summary: 'List a session its journal entries, newest first (F-SES-21)',
    description: 'The session’s journal entries (charges, grants and their reversals), newest first, each with the funding source ' +
        'that paid it. Keyset paginated on `(occurredAt, id)`: entries a running session posts while a client pages land ' +
        'before the first page, so a cursor never repeats or skips an entry.',
    request: { params: SessionParamsSchema, query: PageQuerySchema },
    [PERMISSION_EXTENSION]: SESSIONS_READ,
    responses: {
        200: jsonResponse(SessionLedgerPageSchema, 'A page of the session’s journal entries, newest first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET …/venues/{venueId}/stations/{stationId}/session  — the station's live session (F-SES-21)
// ---------------------------------------------------------------------------------------------
const StationSessionParamsSchema = VenueScopeParamsSchema.extend({
    stationId: StationIdSchema,
}).openapi('StationSessionParams');
export const getStationSessionRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/session',
    operationId: 'getStationSession',
    tags: ['sessions', 'stations'],
    summary: 'The session running on a station, if any (F-SES-21)',
    description: 'The one session that is not ended on this station — what the console’s session drawer opens from a ' +
        'station tile (spec/13 §5). Answers `404` when the station is free.',
    request: { params: StationSessionParamsSchema },
    [PERMISSION_EXTENSION]: SESSIONS_READ,
    responses: {
        200: jsonResponse(SessionSchema, 'The station’s current session.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such station, or it has no active session.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{organizationId}/sessions/{sessionId}/comp  — grant comp (F-SES-16, F-SES-26)
// ---------------------------------------------------------------------------------------------
export const CompSessionSchema = z
    .object({
    minutes: z.number().int().min(1).max(1440),
    /** Why, from the list (spec/05 §7): compensation, promo, staff play, or other with a note. */
    reason: CompReasonSchema,
    /** A note; required with reason `other`, optional otherwise. */
    note: CompNoteSchema.optional(),
})
    .openapi('CompSession');
export const compSessionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/comp',
    operationId: 'compSession',
    tags: ['sessions'],
    summary: 'Grant comp time to a running session (F-SES-16)',
    description: 'Add free minutes to a session, with a reason — the comp funding source. It grants comp seconds to the ' +
        'session’s ledger and rebuilds the plan, which brings a session in grace back to `active` (F-SES-26). ' +
        'Bounded per action and per shift by the lower of `sessions.compMaxMinutes*` and the role’s own limits (spec/07 ' +
        '§6.4); `sessions:comp_unlimited` lifts the setting’s caps, never the role’s. A member’s own comp limit, when set, ' +
        'stands in for the role’s, and a role or member with no comp limit (`compUnlimited`) is bounded by neither. The shift is the venue’s open till ' +
        'shift, or the business day when none is open. A grant past a limit is `409 conflict`. Audited. Comp seconds the session has not used when it ends ' +
        'expire with it (spec/05 §4.1: comp dies with the session). Send an `Idempotency-Key`: a retry with it answers ' +
        'the comp already granted and grants nothing twice.',
    request: {
        params: SessionParamsSchema,
        headers: IdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: CompSessionSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: SESSIONS_COMP,
    [UNLIMITED_BY_EXTENSION]: SESSIONS_COMP_UNLIMITED,
    responses: {
        200: jsonResponse(SessionSchema, 'The session, with the comp time added to its plan.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        409: problemResponse('The session has already ended, or the comp limit is reached.', 'session-already-ended', 'conflict'),
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{organizationId}/sessions/{sessionId}/end  — end (F-SES-14)
// ---------------------------------------------------------------------------------------------
export const EndSessionSchema = z
    .object({
    /** Why staff are ending it; defaults to `staff_ended`. */
    reason: z.enum(['staff_ended', 'account_blocked']).optional(),
})
    .openapi('EndSession');
export const endSessionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/end',
    operationId: 'endSession',
    tags: ['sessions'],
    summary: 'End a session (F-SES-14)',
    description: 'Close the open segment, settle the final charge to the ledger, and end the session. Ending an already ' +
        'ended session returns `409 session-already-ended`; no double charge (spec/05 §14).',
    request: {
        params: SessionParamsSchema,
        body: { content: { 'application/json': { schema: EndSessionSchema } }, required: false },
    },
    [PERMISSION_EXTENSION]: SESSIONS_END,
    responses: {
        200: jsonResponse(SessionSchema, 'The ended session, with what it was charged.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        409: problemResponse('The session has already ended.', 'session-already-ended'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{organizationId}/sessions/{sessionId}/lock  — staff lock a staff-run session (ADR-0033)
// ---------------------------------------------------------------------------------------------
export const LockSessionSchema = z
    .object({
    /** `true` locks the session (the player stepped away; staff hold the station), `false` unlocks it. */
    locked: z.boolean(),
})
    .strict()
    .openapi('LockSession');
export const lockSessionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/lock',
    operationId: 'lockSession',
    tags: ['sessions'],
    summary: 'Lock or unlock a staff-run session (ADR-0033)',
    description: 'The step-away lock (spec/05 §6), done by staff for a session on a staff-run station, which has no kiosk to lock ' +
        'from. Billing behind the lock follows the session’s `sessions.lockPausesBilling` snapshot and ' +
        '`sessions.maxLockMinutes` ends it, as on a PC. Idempotent: locking a locked session, or unlocking an active one, ' +
        'changes nothing. A session on a managed station locks from the Floor’s Lock (`POST …/stations/{s}/lock`), which ' +
        'tells its app: here it is `409 conflict`. Requires `sessions:lock`. Audited.',
    request: {
        params: SessionParamsSchema,
        body: { content: { 'application/json': { schema: LockSessionSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: SESSIONS_LOCK,
    responses: {
        200: jsonResponse(SessionSchema, 'The session as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        409: problemResponse('The session has already ended, or it is on a managed station.', 'session-already-ended', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{organizationId}/sessions/{sessionId}/sell-time  — sell time (F-SES-17)
// ---------------------------------------------------------------------------------------------
/**
 * Sell more time to a running session (F-SES-17, spec/05 §7). Staff sell a block of minutes at the
 * counter; the block is added to the session's paid funding — a postpaid funding segment at the
 * class's pay-as-you-go rate — and starts funding immediately, extending the funded end and bringing
 * a session in grace back to `active` (spec/05 §4.5). The charge accrues to the player's on-account
 * receivable as the time is played, the same as any postpaid segment (spec/06 §8.6): the sale is the
 * order, the segment is where it is billed.
 */
export const SellSessionTimeSchema = z
    .object({
    /** How many minutes of play to sell onto this session, at the class's pay-as-you-go rate. */
    minutes: z.number().int().min(1).max(1440),
})
    .openapi('SellSessionTime');
export const sellSessionTimeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/sell-time',
    operationId: 'sellSessionTime',
    tags: ['sessions'],
    summary: 'Sell more time to a running session (F-SES-17)',
    description: 'Add paid minutes to a running session at its pay-as-you-go rate. The new time starts funding at ' +
        'once — a postpaid funding segment charged to the player on account as it is played — extending the ' +
        'funded end and recovering a session in grace (spec/05 §4.5, §7). Audited.',
    request: {
        params: SessionParamsSchema,
        body: { content: { 'application/json': { schema: SellSessionTimeSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    responses: {
        200: jsonResponse(SessionSchema, 'The session, with the sold time added to its plan.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        409: problemResponse('The session has already ended.', 'session-already-ended'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{organizationId}/sessions/{sessionId}/message  — message the player (F-SES-20)
// ---------------------------------------------------------------------------------------------
export const MessageSessionSchema = z
    .object({
    /** The line to show the player at the station — a kiosk toast and the in-game overlay. */
    text: z.string().min(1).max(280),
    /** Ask the player to acknowledge it before it clears (spec/05 §7). Defaults to false. */
    needsAck: z.boolean().optional(),
})
    .openapi('MessageSession');
export const messageSessionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/message',
    operationId: 'messageSession',
    tags: ['sessions', 'stations'],
    summary: 'Message the player at their station (F-SES-20)',
    description: 'Send a short line to the player, shown as a kiosk toast and the in-game overlay, optionally ' +
        'requiring an acknowledgement (spec/05 §7). Delivered as a `session.message` command to the station ' +
        'through the realtime gateway (spec/09 §6.2); recorded on the session timeline (F-SES-21). Audited.',
    request: {
        params: SessionParamsSchema,
        body: { content: { 'application/json': { schema: MessageSessionSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: STATIONS_MESSAGE,
    responses: {
        200: jsonResponse(SessionEventSchema, 'The timeline entry recorded for the message.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such session in this organisation.', 'not-found'),
        409: problemResponse('The session has already ended.', 'session-already-ended'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/organizations/{organizationId}/sessions/{sessionId}/move  — move to another station (F-SES-18)
// ---------------------------------------------------------------------------------------------
//
// One session row moves: `session.stationId` is updated in place, a `move` timeline entry records both
// stations and the actor, and the session is never ended (spec/05 §7). Moving to a station of a
// different class raises warnings the console shows before it confirms — the passes funding the session
// may not apply to the new class, and the new class may carry a higher age limit (F-ORG-03 rule 8). A
// call with `confirm` absent or false **previews** those warnings without moving; with `confirm: true`
// it moves and returns the moved session. An underage player is refused the move with `age-restricted`.
/** A warning a cross-class move raises, for the console to show before it confirms (spec/05 §7). */
export const SessionMoveWarningSchema = z
    .object({
    code: z.enum(['passes-may-not-apply', 'higher-age-limit']),
    message: z.string().min(1).max(200),
})
    .openapi('SessionMoveWarning');
export const MoveSessionSchema = z
    .object({
    /** The station to move the session to — of this venue, free and enabled. */
    stationId: StationIdSchema,
    /** Confirm the move. Absent or false previews the warnings without moving (spec/05 §7). */
    confirm: z.boolean().optional(),
})
    .openapi('MoveSession');
export const SessionMoveResultSchema = z
    .object({
    /** True when the session was moved; false for a preview that returned the warnings only. */
    moved: z.boolean(),
    /** The warnings a cross-class move raises, empty for a move within the same class. */
    warnings: z.array(SessionMoveWarningSchema),
    /** The session — on the new station when `moved`, otherwise unchanged. */
    session: SessionSchema,
})
    .openapi('SessionMoveResult');
export const moveSessionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/sessions/{sessionId}/move',
    operationId: 'moveSession',
    tags: ['sessions'],
    summary: 'Move a running session to another station (F-SES-18)',
    description: 'Move the one session row to another station of the venue (spec/05 §7): `confirm` absent or false ' +
        'returns the warnings a cross-class move raises — passes may not apply, a higher age limit — without ' +
        'moving, so the console can show them; `confirm: true` moves the session and returns it. The session ' +
        'is never ended and the player’s time left does not reset. An underage player is refused with ' +
        '`age-restricted` (spec/05 §3.2 check 5).',
    request: {
        params: SessionParamsSchema,
        body: { content: { 'application/json': { schema: MoveSessionSchema } }, required: true },
    },
    [PERMISSION_EXTENSION]: SESSIONS_MOVE,
    responses: {
        200: jsonResponse(SessionMoveResultSchema, 'The warnings, and the session (moved when confirmed).'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Not allowed, or the player does not meet the target class’s age limit (spec/05 §3.2 check 5).', 'forbidden', 'second-factor-required', 'age-restricted'),
        404: problemResponse('No such session or target station in this organisation.', 'not-found'),
        409: problemResponse('The session has already ended, or the target station is unavailable.', 'session-already-ended', 'station-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The station's own surface: kiosk sign-in start and the player's logout (F-SES-01, F-SES-14)
// ---------------------------------------------------------------------------------------------
export const StationStartSessionSchema = z
    .object({
    playerId: PlayerIdSchema,
    /**
     * How to fund it. Omitted, the session funds from the venue's default pay-as-you-go budget
     * (`sessions.paygDefaultBudgetAmount`); when nothing funds it, the start is refused with
     * `no-funding` (spec/05 §3.2 check 8).
     */
    funding: FundingChoiceSchema.optional(),
    /**
     * The reservation this kiosk sign-in checks in against (F-SES-05, spec/05 §10). Optional, and
     * recorded on the `started` timeline entry; validation lands with the `reservations` module (F-RES).
     */
    reservationId: ReservationIdSchema.optional(),
    /**
     * Pass first (the default) or wallet first — the kiosk's "Use wallet, save my pass" (spec/05 §4.2). `wallet` is an
     * explicit choice: it turns the player's "Use my wallet for play time" back on when they had turned it off.
     */
    prefer: SessionFundingPreferenceSchema.optional(),
})
    .openapi('StationStartSession');
export const startStationSessionRoute = createRoute({
    method: 'post',
    path: '/v1/station/sessions',
    operationId: 'startStationSession',
    tags: ['sessions', 'stations'],
    summary: 'Start a session from the PC the player signed in on (F-SES-01)',
    description: `${STATION_AUTHENTICATION} The signed-in player starts a per-minute session on this station ` +
        '(spec/05 §3.1). Runs the pre-start checks and returns the session, the plan and time left.',
    request: {
        body: { content: { 'application/json': { schema: StationStartSessionSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(SessionSchema, 'The session, with its plan and time left.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        402: problemResponse('Nothing funds the session (spec/05 §3.2 check 8).', 'no-funding'),
        403: problemResponse('The player does not meet the station class’s age limit (spec/05 §3.2 check 5).', 'age-restricted'),
        404: problemResponse('No such player.', 'not-found'),
        409: problemResponse('The station is unavailable, or the player is already in a session.', 'station-unavailable', 'already-in-session'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const endStationSessionRoute = createRoute({
    method: 'post',
    path: '/v1/station/sessions/current/end',
    operationId: 'endStationSession',
    tags: ['sessions', 'stations'],
    summary: 'The player logs out at the PC (F-SES-14)',
    description: `${STATION_AUTHENTICATION} Ends the station's own active session — the player's logout — settling the ` +
        'final charge to the ledger (`endReason: logout`). Answers `404` when the station has no active session.',
    responses: {
        200: jsonResponse(SessionSchema, 'The ended session, with what it was charged.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/station/sessions/current/add-time  — the player buys more time at the PC (F-KIO-13)
// ---------------------------------------------------------------------------------------------
//
// The signed-in player, from the kiosk, adds paid minutes to the station's own running session. It is
// the station-authenticated, self-service twin of the staff `sell-time` route (F-SES-17): the minutes
// are added at the class pay-as-you-go rate as a postpaid segment — funding starts at once (recovering a
// session in grace) and is charged as it is played, settled at logout. Wallet-prepaid add-time (charge a
// wallet balance up front) rides in later, once the wallet-spend-for-time primitive lands; until then the
// PC's "add time" is postpaid, exactly like the staff action.
export const AddTimeStationSessionSchema = z
    .object({
    /** How many minutes of play to add to this session, at the class's pay-as-you-go rate. */
    minutes: z.number().int().min(1).max(1440),
})
    .openapi('AddTimeStationSession');
/**
 * The kiosk's attempt id (a UUID minted when the player taps an option) as the `Idempotency-Key`: a re-sent
 * intent or a double tap answers the time already added instead of adding it twice (F-KIO-13). Optional only so
 * an older station that sends none still adds time.
 */
const StationAddTimeHeadersSchema = z
    .object({ 'idempotency-key': IdempotencyKeySchema.optional() })
    .openapi('StationAddTimeHeaders');
export const addTimeStationSessionRoute = createRoute({
    method: 'post',
    path: '/v1/station/sessions/current/add-time',
    operationId: 'addTimeStationSession',
    tags: ['sessions', 'stations'],
    summary: 'The player buys more time at the PC (F-KIO-13)',
    description: `${STATION_AUTHENTICATION} The signed-in player adds paid minutes to this station's own active session ` +
        'at the class pay-as-you-go rate — a postpaid segment that funds at once (recovering a session in grace) ' +
        'and is charged as it is played, settled at logout (spec/05 §4.5, §7; F-KIO-13). The station-authenticated, ' +
        'self-service twin of `sell-time`. Answers `404` when the station has no active session. Audited. With an ' +
        '`Idempotency-Key`, a retry of the same attempt answers the session as it stands and adds nothing. ' +
        'Keeping on with the wallet is `useWalletStationSession`.',
    request: {
        headers: StationAddTimeHeadersSchema,
        body: { content: { 'application/json': { schema: AddTimeStationSessionSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SessionSchema, 'The session, with the added time in its plan.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session.', 'not-found'),
        409: problemResponse('The session has already ended.', 'session-already-ended'),
        422: problemResponse('The same key was already used for a different add.', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/station/sessions/current/use-wallet  — "Keep playing with wallet" (F-KIO-13, F-SES-22)
// ---------------------------------------------------------------------------------------------
//
// The quick purchase's wallet option (spec/12 §7.4): rather than buying anything, the player's wallet starts
// funding play on this session at the session's rate (spec/05 §4.1) — the plan is rebuilt server-side and the
// funded end moves by what the balance buys. No order and no sale: the wallet is drawn as the time is played,
// like a session that started on it. Idempotent: a session the wallet already funds is re-planned, nothing more.
export const useWalletStationSessionRoute = createRoute({
    method: 'post',
    path: '/v1/station/sessions/current/use-wallet',
    operationId: 'useWalletStationSession',
    tags: ['sessions', 'stations'],
    summary: 'Keep playing with the wallet (F-KIO-13)',
    description: `${STATION_AUTHENTICATION} Turns on the player's wallet as a funding source for this station's own active ` +
        'session at its pay-as-you-go rate and rebuilds the plan (spec/05 §4.1, §4.2): the funded end moves by what ' +
        'the balance buys, a session in grace returns to normal funding, and a new lease is pushed. Creates no order; ' +
        'the wallet is drawn as the time is played. Needs `sessions.walletFundsPlay` and `wallet.enabled`, a ' +
        'registered player and a balance of more than one billing unit. An explicit choice: it turns the player’s "Use my ' +
        'wallet for play time" back on when they had turned it off (`setStationWalletUse`). Idempotent. Audited.',
    responses: {
        200: jsonResponse(SessionSchema, 'The session, with the wallet in its plan.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session.', 'not-found'),
        409: problemResponse('The session has ended, the venue does not fund play from the wallet, or the balance buys no time.', 'session-already-ended', 'wallet-disabled', 'wallet-insufficient-funds'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// PUT /v1/station/sessions/current/wallet-use  — "Use my wallet for play time" (F-WAL-04, F-KIO-15)
// ---------------------------------------------------------------------------------------------
//
// The player's own choice (spec/05 §4.1): whether their wallet pays for play time on its own once their paid time,
// passes and any comp run out. It is scoped like the wallet it governs — one per player per organisation — and on by
// default, so the day it ships nothing changes for a venue whose wallet funds play. The kiosk keeps the station-bridge
// design (#709): it never calls this itself; the station does, device-authenticated, for the player on its own running
// session. Changing it re-plans that session at once, in the same request: what is owed up to now is charged, the
// wallet's share is cut to what it has already paid for (off — the rest of the billing unit it started) or extended by
// what its balance buys now (on), the timeline records it, a new lease is pushed, and the answer carries the session's
// new funded end so the kiosk's clock moves before the lease arrives.
export const StationWalletUseRequestSchema = z
    .object({
    /** On: the wallet pays for play once the paid time and passes run out. Off: only when the player chooses to. */
    enabled: z.boolean(),
})
    .strict()
    .openapi('StationWalletUseRequest', { description: 'Turn the session player’s wallet on or off for play time (F-WAL-04).' });
export const StationWalletUseSchema = z
    .object({
    /** The choice as it now stands. */
    enabled: z.boolean(),
    /** The session player's wallet as the station reads it (`GET …/current/wallet`), with the choice. */
    wallet: StationSessionWalletSchema,
    /** The running session after the re-plan: its new funded end, time left and breakdown. */
    session: SessionSchema,
})
    .openapi('StationWalletUse', { description: 'The player’s wallet use for play time, and the session re-planned on it (F-WAL-04).' });
export const setStationWalletUseRoute = createRoute({
    method: 'put',
    path: '/v1/station/sessions/current/wallet-use',
    operationId: 'setStationWalletUse',
    tags: ['sessions', 'wallet', 'stations'],
    summary: 'The player turns their wallet on or off for play time (F-WAL-04)',
    description: `${STATION_AUTHENTICATION} Saves the player's "Use my wallet for play time" (spec/05 §4.1) — for the player on ` +
        "this station's own running session, at the station's organisation — and re-plans that session at once: what is " +
        'owed up to now is charged first; off, the wallet keeps only what it has already paid for (the rest of the billing ' +
        'unit it started), so the funded end may move in to what the passes and comp cover, or to now and grace; on, it ' +
        'funds what its balance buys at the session’s rate, and a session in grace returns to active. The timeline records ' +
        '`wallet_enabled` or `wallet_disabled`, the change is audited, and a new lease is pushed. Setting what is already ' +
        'set changes nothing. Needs `sessions.walletFundsPlay` and `wallet.enabled`. Explicit wallet actions — "Use wallet, ' +
        'save my pass" at sign-in and `useWalletStationSession` — turn it back on.',
    request: {
        body: { content: { 'application/json': { schema: StationWalletUseRequestSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationWalletUseSchema, 'The choice, the wallet and the session re-planned on it.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session with a player.', 'not-found'),
        409: problemResponse('The session has ended, or the venue does not fund play from the wallet.', 'session-already-ended', 'wallet-disabled'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The station reports a lock / unlock (F-SES-12, F-SES-15; spec/05 §6, §7)
// ---------------------------------------------------------------------------------------------
//
// The player steps away and locks the screen at the kiosk (F-SES-12), or staff lock the station
// remotely (F-SES-15, the floor's `session.lock` command), or the station auto-locks after
// `sessions.autoLockIdleMinutes` of no input. Whichever it is, the station reports the fact to the
// cloud so the session's status follows it (`active` ⇄ `locked`) and the timeline records it. Billing
// continues while locked (spec/05 §6): time left keeps counting, and a session held past
// `sessions.maxLockMinutes` ends with `lock_limit` on the checkpoint tick.
export const LockStationSessionSchema = z
    .object({
    /**
     * Why the screen locked: the player chose Lock (`step_away`), the idle auto-lock fired (`idle`),
     * or staff locked it remotely (`remote`). Defaults to `step_away`. Recorded on the timeline.
     */
    reason: z.enum(['step_away', 'idle', 'remote']).optional(),
    /**
     * The 4–6 digit PIN to set for this session, sent on the **first** lock only (spec/05 §6 rule 1,
     * spec/07 §4.4). It is stored as a salted hash on the session row and verified on unlock; it is
     * never returned, and it is cleared when the session ends. Ignored (the existing PIN kept) when the
     * session already has one, and absent for a re-lock. A player who locks with no PIN — the idle
     * auto-lock of an account that never set one — sets it at the moment they first unlock.
     */
    pin: z
        .string()
        .regex(SESSION_PIN_PATTERN, `A PIN is ${SESSION_PIN_MIN_LENGTH}–${SESSION_PIN_MAX_LENGTH} digits.`)
        .optional(),
})
    .openapi('LockStationSession');
/**
 * How the player unlocked a locked session (spec/05 §6, spec/12 §11). The `pin` method is verified by
 * the api against the session's stored hash, with the five-wrong-tries lockout (spec/05 §6 rule 5);
 * `password` and `phone` are verified by the kiosk's own re-authentication before it reports the
 * unlock (the same station-trust model the sign-in routes use), so they carry no secret here. Staff
 * unlocking from the console reports the unlock too, and clears any PIN penalty (F-SES-15 rule 4).
 */
export const UnlockStationSessionSchema = z
    .object({
    method: z.enum(SESSION_UNLOCK_METHODS).optional(),
    /** The PIN, required only for the `pin` method; verified by the api. */
    pin: z
        .string()
        .regex(SESSION_PIN_PATTERN, `A PIN is ${SESSION_PIN_MIN_LENGTH}–${SESSION_PIN_MAX_LENGTH} digits.`)
        .optional(),
})
    .openapi('UnlockStationSession');
export const lockStationSessionRoute = createRoute({
    method: 'post',
    path: '/v1/station/sessions/current/lock',
    operationId: 'lockStationSession',
    tags: ['sessions', 'stations'],
    summary: 'The station reports its session locked (F-SES-12)',
    description: `${STATION_AUTHENTICATION} Moves the station's active session to \`locked\` — the player stepped away, the ` +
        'idle auto-lock fired, or staff locked it remotely (spec/05 §6). On the first lock the body carries the 4–6 ' +
        'digit `pin` to set for the session (stored hashed, verified on unlock). Billing continues; recorded on the ' +
        'timeline. Answers `404` when the station has no active session, and is idempotent when it is already locked.',
    request: {
        body: { content: { 'application/json': { schema: LockStationSessionSchema } }, required: false },
    },
    responses: {
        200: jsonResponse(SessionSchema, 'The session, now locked.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session.', 'not-found'),
        409: problemResponse('The session has already ended.', 'session-already-ended'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const unlockStationSessionRoute = createRoute({
    method: 'post',
    path: '/v1/station/sessions/current/unlock',
    operationId: 'unlockStationSession',
    tags: ['sessions', 'stations'],
    summary: 'The station reports its session unlocked (F-SES-12)',
    description: `${STATION_AUTHENTICATION} The player unlocks with their PIN, password or phone (or staff unlock it ` +
        'remotely). The `pin` method is verified by the api against the session’s stored hash, with the ' +
        'five-wrong-tries lockout (spec/05 §6 rule 5): a wrong PIN answers `session-pin-incorrect` (with the tries ' +
        'left), and the sixth inside the penalty window `session-unlock-locked` (with `retryAfterSeconds`). On success ' +
        'the session returns to `active`, any penalty clears, and the timeline records it. Answers `404` when the ' +
        'station has no active session, and is idempotent when it is not locked.',
    request: {
        body: { content: { 'application/json': { schema: UnlockStationSessionSchema } }, required: false },
    },
    responses: {
        200: jsonResponse(SessionSchema, 'The session, now active.'),
        400: problemResponse('The PIN is not right (spec/05 §6).', 'validation-failed', 'session-pin-incorrect'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session.', 'not-found'),
        409: problemResponse('The session has already ended.', 'session-already-ended'),
        429: problemResponse('Unlocking is locked after five wrong PINs (spec/05 §6).', 'session-unlock-locked'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The station reports an end it made itself (spec/05 §8.5; F-SES-23)
// ---------------------------------------------------------------------------------------------
//
// A station ends a session on its own when the lease runs out while it is offline, or when on reconnect
// it finds the cloud no longer runs the session it held. The server must hear it, or billing runs on:
// the station queues the report durably and delivers it when it can. The server ends the session at the
// reported moment (idempotently) and never charges past it — play already charged beyond it is reversed.
export const StationSessionEndReasonSchema = z
    .enum(['time_up_offline', 'station_reconciled', 'logout_offline'])
    .openapi('StationSessionEndReason', { description: 'Why the station ended a session itself (spec/05 §8.5).' });
export const StationSessionEndReportSchema = z
    .object({
    reason: StationSessionEndReasonSchema,
    /** When play stopped on the station, RFC 3339 UTC. Clamped to the session's start and the server's now. */
    endedAt: IsoDateTimeSchema,
    /**
     * Seconds the session ran past its lease's funded end while offline, under "keep running sessions going"
     * (`continue_and_settle`, spec/05 §8.1): charged on this report at the session's rate — the wallet when it
     * funds play, else the player's account — less the grace the session would have had online, capped by the
     * lease's `maxOverrunSeconds`. Absent or 0 for none.
     */
    overrunSeconds: z.int().min(0).max(86_400).optional(),
    /** Seconds the session ran while the station was offline, for the console's "Played offline" mark. */
    offlineSeconds: z.int().min(0).max(86_400).optional(),
})
    .openapi('StationSessionEndReport');
const StationSessionReportParamsSchema = z.object({ sessionId: SessionIdSchema }).openapi('StationSessionReportParams');
export const reportStationSessionEndRoute = createRoute({
    method: 'post',
    path: '/v1/station/sessions/{sessionId}/end-report',
    operationId: 'reportStationSessionEnd',
    tags: ['sessions', 'stations'],
    summary: 'The station reports a session it ended itself (F-SES-23)',
    description: `${STATION_AUTHENTICATION} Ends the named session — one this station ran — at \`endedAt\`, and charges ` +
        'nothing past it: play the checkpoint already charged beyond it is reversed to where it came from ' +
        '(the wallet, the pass, the account), audited. Idempotent: a report already applied changes nothing.',
    request: {
        params: StationSessionReportParamsSchema,
        body: { content: { 'application/json': { schema: StationSessionEndReportSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(SessionSchema, 'The session as it now stands: ended, charged to the reported end.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station ran no such session.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** A fresh signed lease for the station's running session (spec/05 §8.2). */
export const StationSessionLeaseSchema = z
    .object({
    sessionId: SessionIdSchema,
    /** A compact JWS whose payload is the `SessionLease` (the same form `session.lease` carries). */
    lease: z.string().min(1),
})
    .openapi('StationSessionLease');
export const getStationSessionLeaseRoute = createRoute({
    method: 'get',
    path: '/v1/station/sessions/current/lease',
    operationId: 'getStationSessionLease',
    tags: ['sessions', 'stations'],
    summary: 'A fresh lease for the station’s running session (F-SES-23)',
    description: `${STATION_AUTHENTICATION} Signs and returns the lease for the station's active session as it stands now. ` +
        'The station reads it when a `session.lease` command arrives without a lease — the worker, which holds no ' +
        'signing key, saying the funded end moved (a top-up, a pass window, an expiry).',
    responses: {
        200: jsonResponse(StationSessionLeaseSchema, 'The signed lease.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('This station has no active session.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** Whether a signed-in player could choose between a pass and the wallet at the start (spec/05 §4.2). */
export const StationFundingOptionsSchema = z
    .object({
    /** A pass that covers this station now. */
    pass: z.boolean(),
    /** A wallet that funds play here (`sessions.walletFundsPlay` on, a balance of more than one billing unit). */
    wallet: z.boolean(),
    /**
     * The player's "Use my wallet for play time" (spec/05 §4.1, F-WAL-04): whether the wallet would pay on its own after
     * the pass. With it off, "Use my pass" leaves the wallet out and "Use wallet, save my pass" turns it back on — the
     * kiosk says so. Absent from an older api: read as on.
     */
    walletUseForPlay: z.boolean().optional(),
})
    .openapi('StationFundingOptions');
export const StationFundingOptionsQuerySchema = z.object({ playerId: PlayerIdSchema }).openapi('StationFundingOptionsQuery');
export const getStationFundingOptionsRoute = createRoute({
    method: 'get',
    path: '/v1/station/funding-options',
    operationId: 'getStationFundingOptions',
    tags: ['sessions', 'stations'],
    summary: 'Whether the signed-in player can choose pass or wallet (F-SES-09)',
    description: 'Station-authenticated with the kiosk token (or the device token). After a sign-in, the kiosk asks whether ' +
        'the player holds both a pass that covers this station now and a wallet that funds play, so it can offer ' +
        '"Use my pass" / "Use wallet, save my pass" before the session starts.',
    request: { query: StationFundingOptionsQuerySchema },
    responses: {
        200: jsonResponse(StationFundingOptionsSchema, 'What could fund the player here now.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const SESSION_ROUTES = [
    startSessionRoute,
    getStationSessionRoute,
    getSessionStatusRoute,
    getSessionTimelineRoute,
    listSessionLedgerRoute,
    compSessionRoute,
    sellSessionTimeRoute,
    messageSessionRoute,
    moveSessionRoute,
    endSessionRoute,
    lockSessionRoute,
    startStationSessionRoute,
    endStationSessionRoute,
    useWalletStationSessionRoute,
    setStationWalletUseRoute,
    lockStationSessionRoute,
    unlockStationSessionRoute,
    reportStationSessionEndRoute,
    getStationSessionLeaseRoute,
    getStationFundingOptionsRoute,
];
//# sourceMappingURL=sessions.js.map